import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const ExternalChangeOrders = () => {
  const { projectVuid, contractVuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [externalChangeOrders, setExternalChangeOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingChangeOrder, setEditingChangeOrder] = useState(null);
  const [selectedChangeOrder, setSelectedChangeOrder] = useState(null);
  const [showLines, setShowLines] = useState(false);
  const [changeOrderLines, setChangeOrderLines] = useState([]);
  const [projects, setProjects] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [costTypes, setCostTypes] = useState([]);
  const [accountingPeriods, setAccountingPeriods] = useState([]);
  const [projectBudgetLines, setProjectBudgetLines] = useState([]);
  
  // Retrieve from Integration state
  const [showRetrieveModal, setShowRetrieveModal] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [retrievingChangeOrders, setRetrievingChangeOrders] = useState(false);
  const [retrievedChangeOrders, setRetrievedChangeOrders] = useState([]);
  const [retrieveError, setRetrieveError] = useState('');
  
  // Import state
  const [selectedChangeOrders, setSelectedChangeOrders] = useState([]);
  const [importingChangeOrders, setImportingChangeOrders] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  
  // Ref for Excel grid focus
  const excelGridRef = useRef(null);
  
  // Get project VUID from URL params or search params for form initialization
  const searchParams = new URLSearchParams(location.search);
  const urlProjectVuid = searchParams.get('project');
  const effectiveProjectVuid = projectVuid || urlProjectVuid;
  
  // Form state
  const [formData, setFormData] = useState({
    project_vuid: effectiveProjectVuid || '',
    contract_vuid: contractVuid || '',
    accounting_period_vuid: '',
    change_order_number: '',
    description: '',
    change_order_date: new Date().toISOString().split('T')[0],
    status: 'pending',
    notes: ''
  });
  
  // Edit form state
  const [editFormData, setEditFormData] = useState({
    project_vuid: '',
    contract_vuid: '',
    accounting_period_vuid: '',
    change_order_number: '',
    description: '',
    change_order_date: '',
    status: '',
    notes: ''
  });
  
  // Line item form state
  const [lineFormData, setLineFormData] = useState({
    cost_code_vuid: '',
    cost_type_vuid: '',
    contract_amount_change: '',
    budget_amount_change: '',
    notes: ''
  });

  const lineCostCodeSelectOptions = useMemo(() => {
    if (projectBudgetLines.length === 0) {
      return costCodes
        .filter(c => c.status === 'active')
        .map((cc) => ({
          vuid: cc.vuid,
          code: cc.code,
          description: cc.description,
        }));
    }
    const byVuid = new Map();
    const budgetCostCodeVuids = new Set(
      projectBudgetLines.map((bl) => bl.cost_code_vuid).filter(Boolean)
    );
    costCodes.filter(c => c.status === 'active').forEach((cc) => {
      if (budgetCostCodeVuids.has(cc.vuid)) {
        byVuid.set(cc.vuid, {
          vuid: cc.vuid,
          code: cc.code,
          description: cc.description,
        });
      }
    });
    projectBudgetLines.forEach((bl) => {
      if (!bl.cost_code_vuid || byVuid.has(bl.cost_code_vuid)) return;
      if (bl.cost_code) {
        byVuid.set(bl.cost_code_vuid, {
          vuid: bl.cost_code_vuid,
          code: bl.cost_code.code,
          description: bl.cost_code.description || bl.cost_code.code,
        });
      }
    });
    changeOrderLines.forEach((line) => {
      if (!line.cost_code_vuid || byVuid.has(line.cost_code_vuid)) return;
      if (line.cost_code) {
        byVuid.set(line.cost_code_vuid, {
          vuid: line.cost_code_vuid,
          code: line.cost_code.code,
          description: line.cost_code.description || line.cost_code.code,
        });
      }
    });
    return Array.from(byVuid.values());
  }, [costCodes, projectBudgetLines, changeOrderLines]);

  useEffect(() => {
    fetchData();
  }, [projectVuid, contractVuid, location.search]);

  const fetchCostDimensionsIfNeeded = async () => {
    if (costCodes.length > 0 && costTypes.length > 0) {
      return;
    }
    try {
      const [costCodesResponse, costTypesResponse] = await Promise.all([
        axios.get(`${baseURL}/api/costcodes`),
        axios.get(`${baseURL}/api/costtypes`),
      ]);
      setCostCodes(costCodesResponse.data);
      setCostTypes(costTypesResponse.data);
    } catch (err) {
      console.error('Error fetching cost codes/types:', err);
    }
  };

  // Auto-prefill contract and accounting period when there's only one option
  useEffect(() => {
    if (!showCreateForm) return;
    
    // Get the current project from formData
    const currentProjectVuid = formData.project_vuid;
    
    // Filter contracts for the current project
    const projectContracts = contracts.filter(contract => 
      contract.project_vuid === currentProjectVuid
    );
    
    // Filter open accounting periods
    const openAccountingPeriods = accountingPeriods.filter(period => 
      period.status === 'open'
    );
    
    // Update formData if we need to prefill
    setFormData(prev => {
      const updates = {};
      
      // Prefill contract if there's only one for this project and it's not already set
      if (projectContracts.length === 1 && !prev.contract_vuid) {
        updates.contract_vuid = projectContracts[0].vuid;
      }
      
      // Prefill accounting period if there's only one open and it's not already set
      if (openAccountingPeriods.length === 1 && !prev.accounting_period_vuid) {
        updates.accounting_period_vuid = openAccountingPeriods[0].vuid;
      }
      
      // Only update if there are changes
      if (Object.keys(updates).length > 0) {
        return { ...prev, ...updates };
      }
      
      return prev;
    });
  }, [showCreateForm, contracts, accountingPeriods, formData.project_vuid]);



  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Get project VUID from URL params or search params
      const searchParams = new URLSearchParams(location.search);
      const urlProjectVuid = searchParams.get('project');
      const effectiveProjectVuid = projectVuid || urlProjectVuid;
      
      let ecoUrl = `${baseURL}/api/external-change-orders`;
      if (effectiveProjectVuid) {
        ecoUrl += `?project_vuid=${effectiveProjectVuid}`;
      }
      if (contractVuid) {
        ecoUrl += effectiveProjectVuid ? `&contract_vuid=${contractVuid}` : `?contract_vuid=${contractVuid}`;
      }

      const projectsPromise = effectiveProjectVuid
        ? axios
            .get(`${baseURL}/api/projects/${effectiveProjectVuid}`)
            .then((r) => ({ data: [r.data] }))
            .catch(() => ({ data: [] }))
        : axios.get(`${baseURL}/api/projects`);

      const contractsUrl = effectiveProjectVuid
        ? `${baseURL}/api/project-contracts?project_vuid=${effectiveProjectVuid}`
        : `${baseURL}/api/project-contracts`;

      const budgetLinesPromise = effectiveProjectVuid
        ? axios.get(`${baseURL}/api/projects/${effectiveProjectVuid}/budget-lines`).catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] });

      const [
        ecoResponse,
        projectsResponse,
        contractsResponse,
        accountingPeriodsResponse,
        budgetResponse,
        integrationsResponse,
      ] = await Promise.all([
        axios.get(ecoUrl),
        projectsPromise,
        axios.get(contractsUrl),
        axios.get(`${baseURL}/api/accounting-periods`),
        budgetLinesPromise,
        axios.get(`${baseURL}/api/integrations`).catch(() => ({ data: [] })),
      ]);
      
      setExternalChangeOrders(ecoResponse.data);
      setProjects(projectsResponse.data);
      setContracts(contractsResponse.data);
      setAccountingPeriods(accountingPeriodsResponse.data);
      setProjectBudgetLines(budgetResponse.data || []);
      setIntegrations(
        (integrationsResponse.data || []).filter(
          (i) => i.integration_type === 'procore' && i.status === 'active'
        )
      );
      
    } catch (err) {
      setError('Error fetching data: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChangeOrder = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${baseURL}/api/external-change-orders`, formData);
      setShowCreateForm(false);
      // Set default accounting period if only one is open
      let defaultAccountingPeriod = '';
      if (accountingPeriods.length === 1 && accountingPeriods[0].status === 'open') {
        defaultAccountingPeriod = accountingPeriods[0].vuid;
      }
      
      setFormData({
        project_vuid: projectVuid || '',
        contract_vuid: contractVuid || '',
        accounting_period_vuid: defaultAccountingPeriod,
        change_order_number: '',
        description: '',
        change_order_date: new Date().toISOString().split('T')[0],
        status: 'pending',
        notes: ''
      });
      setSuccessMessage('External change order created successfully!');
      
      // Automatically show the lines form for the newly created change order
      const newChangeOrder = response.data.change_order;
      setSelectedChangeOrder(newChangeOrder);
      setShowLines(true);
      await Promise.all([
        fetchCostDimensionsIfNeeded(),
        fetchChangeOrderLines(newChangeOrder.vuid),
      ]);
      
      // Don't refresh the change orders list - stay on the lines form
      // The new change order will be added to the list when the lines form is closed
    } catch (err) {
      setError('Error creating external change order: ' + (err.response?.data?.error || err.message));
    }
  };



  const handleAddNewLine = () => {
    // Add the current line form data to the lines array
    if (!lineFormData.cost_code_vuid || !lineFormData.cost_type_vuid || !lineFormData.contract_amount_change) {
      setError('Cost Code, Cost Type, and Contract Amount Change are required');
      return;
    }
    
    const newLine = {
      vuid: `temp-${Date.now()}`,
      cost_code_vuid: lineFormData.cost_code_vuid,
      cost_type_vuid: lineFormData.cost_type_vuid,
      contract_amount_change: lineFormData.contract_amount_change,
      budget_amount_change: lineFormData.budget_amount_change || 0, // Default to 0 if not provided
      notes: lineFormData.notes || '',
      status: 'active'
    };
    
    setChangeOrderLines([...changeOrderLines, newLine]);
    
    // Reset the form
    setLineFormData({
      cost_code_vuid: '',
      cost_type_vuid: '',
      contract_amount_change: '',
      budget_amount_change: '',
      notes: ''
    });
    
    setSuccessMessage('Line added to grid. Click "Save All Lines" to commit changes.');
  };

  const handleClearNewLine = () => {
    setLineFormData({
      cost_code_vuid: '',
      cost_type_vuid: '',
      contract_amount_change: '',
      budget_amount_change: '',
      notes: ''
    });
    setError(''); // Clear any errors
    setSuccessMessage('New line form cleared.');
  };

  const handleLineUpdate = (lineVuid, field, value) => {
    setChangeOrderLines(prevLines => 
      prevLines.map(line => {
        if (line.vuid === lineVuid) {
          // If cost code is changing, clear the cost type
          if (field === 'cost_code_vuid') {
            return { ...line, [field]: value, cost_type_vuid: '' };
          }
          return { ...line, [field]: value };
        }
        return line;
      })
    );
  };

  const handleSaveAllLines = async () => {
    console.log('handleSaveAllLines called');
    console.log('selectedChangeOrder:', selectedChangeOrder);
    console.log('changeOrderLines:', changeOrderLines);
    console.log('lineFormData:', lineFormData);
    
    if (!selectedChangeOrder) {
      console.error('No selectedChangeOrder');
      setError('Error: No change order selected. Please select a change order first.');
      return;
    }
    
    if (!selectedChangeOrder.vuid) {
      console.error('selectedChangeOrder.vuid is undefined');
      console.error('selectedChangeOrder object:', JSON.stringify(selectedChangeOrder, null, 2));
      setError('Error: Change order ID is missing. Please try again.');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      
      // Build the list of lines to save
      const linesToSave = [...changeOrderLines];
      
      // Check if there's data in the new line form that needs to be added
      if (lineFormData.cost_code_vuid && lineFormData.cost_type_vuid && lineFormData.contract_amount_change) {
        const newLineItem = {
          vuid: `temp-${Date.now()}-${Math.random()}`,
          cost_code_vuid: lineFormData.cost_code_vuid,
          cost_type_vuid: lineFormData.cost_type_vuid,
          contract_amount_change: parseFloat(lineFormData.contract_amount_change) || 0,
          budget_amount_change: parseFloat(lineFormData.budget_amount_change) || 0,
          notes: lineFormData.notes || '',
          status: 'active'
        };
        linesToSave.push(newLineItem);
        
        // Clear the form
        setLineFormData({
          cost_code_vuid: '',
          cost_type_vuid: '',
          contract_amount_change: '',
          budget_amount_change: '',
          notes: ''
        });
      }
      
      if (linesToSave.length === 0) {
        setSuccessMessage('No lines to save.');
        setLoading(false);
        return;
      }
      
      console.log('Saving lines:', linesToSave.length);
      
      // Process all lines - save new ones and update existing ones
      let savedCount = 0;
      let updatedCount = 0;
      let errorCount = 0;
      
      for (const line of linesToSave) {
        try {
          const lineData = {
            cost_code_vuid: line.cost_code_vuid,
            cost_type_vuid: line.cost_type_vuid,
            contract_amount_change: parseFloat(line.contract_amount_change) || 0,
            budget_amount_change: parseFloat(line.budget_amount_change) || 0,
            notes: line.notes || '',
            status: line.status || 'active'
          };
          
          console.log('Saving line:', line.vuid, lineData);
          
          if (line.vuid.startsWith('temp-')) {
            // This is a new line, create it
            const response = await axios.post(
              `${baseURL}/api/external-change-orders/${selectedChangeOrder.vuid}/lines`,
              lineData
            );
            console.log('Line created:', response.data);
            savedCount++;
          } else {
            // This is an existing line, update it
            const response = await axios.put(
              `${baseURL}/api/external-change-orders/${selectedChangeOrder.vuid}/lines/${line.vuid}`,
              lineData
            );
            console.log('Line updated:', response.data);
            updatedCount++;
          }
        } catch (lineErr) {
          console.error('Error saving individual line:', lineErr);
          errorCount++;
          setError(`Error saving line: ${lineErr.response?.data?.error || lineErr.message}. Some lines may not have been saved.`);
          // Continue saving other lines even if one fails
        }
      }
      
      // Refresh the data
      await fetchChangeOrderLines(selectedChangeOrder.vuid);
      await fetchData();
      
      if (errorCount === 0) {
        setSuccessMessage(`All lines saved successfully! (${savedCount} new, ${updatedCount} updated)`);
      } else {
        setError(`${errorCount} line(s) failed to save. ${savedCount} new and ${updatedCount} updated successfully.`);
      }
    } catch (err) {
      console.error('Error in handleSaveAllLines:', err);
      setError('Error saving lines: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleShowLines = async (changeOrder) => {
    setSelectedChangeOrder(changeOrder);
    setShowLines(true);
    await Promise.all([
      fetchCostDimensionsIfNeeded(),
      fetchChangeOrderLines(changeOrder.vuid),
    ]);
    
    // Focus on the Excel grid after it renders
    setTimeout(() => {
      if (excelGridRef.current) {
        excelGridRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
        // Focus on the first cost code dropdown in the new line row
        const firstCostCodeSelect = excelGridRef.current.querySelector('select[value=""]');
        if (firstCostCodeSelect) {
          firstCostCodeSelect.focus();
        }
      }
    }, 100);
  };

  const fetchChangeOrderLines = async (changeOrderVuid) => {
    try {
      const response = await axios.get(`${baseURL}/api/external-change-orders/${changeOrderVuid}/lines`);
      setChangeOrderLines(response.data);
    } catch (err) {
      setError('Error fetching change order lines: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteLine = async (lineVuid) => {
    if (!selectedChangeOrder) return;
    
    if (!window.confirm('Are you sure you want to delete this line?')) return;
    
    // Check if this is a temporary line (not yet saved)
    if (lineVuid.startsWith('temp-')) {
      // Remove from local state only
      setChangeOrderLines(prevLines => prevLines.filter(line => line.vuid !== lineVuid));
      setSuccessMessage('Line removed from grid.');
      return;
    }
    
    // This is a saved line, delete from backend
    try {
      await axios.delete(`${baseURL}/api/external-change-orders/${selectedChangeOrder.vuid}/lines/${lineVuid}`);
      setSuccessMessage('Line item deleted successfully!');
      await fetchChangeOrderLines(selectedChangeOrder.vuid);
      // Refresh the change orders to get updated total
      await fetchData();
    } catch (err) {
      setError('Error deleting line item: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteChangeOrder = async (changeOrderVuid) => {
    if (!window.confirm('Are you sure you want to delete this external change order?')) return;
    
    try {
      await axios.delete(`${baseURL}/api/external-change-orders/${changeOrderVuid}`);
      setSuccessMessage('External change order deleted successfully!');
      fetchData();
    } catch (err) {
      setError('Error deleting external change order: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEditChangeOrder = (changeOrder) => {
    setEditingChangeOrder(changeOrder);
    setEditFormData({
      project_vuid: changeOrder.project_vuid || '',
      contract_vuid: changeOrder.contract_vuid || '',
      accounting_period_vuid: changeOrder.accounting_period_vuid || '',
      change_order_number: changeOrder.change_order_number || '',
      description: changeOrder.description || '',
      change_order_date: changeOrder.change_order_date || '',
      status: changeOrder.status || '',
      notes: changeOrder.notes || ''
    });
    setShowEditForm(true);
  };

  // Check if the accounting period for the change order being edited is closed
  const isAccountingPeriodClosed = () => {
    if (!editingChangeOrder || !editingChangeOrder.accounting_period_vuid) {
      return false;
    }
    // Check if the period is in the accountingPeriods list and is closed
    const period = accountingPeriods.find(p => p.vuid === editingChangeOrder.accounting_period_vuid);
    if (period) {
      return period.status === 'closed';
    }
    // If period data is included in the change order object
    if (editingChangeOrder.accounting_period) {
      return editingChangeOrder.accounting_period.status === 'closed';
    }
    return false;
  };

  const handleRetrieveChangeOrders = async () => {
    if (!selectedIntegration) {
      setRetrieveError('Please select an integration');
      return;
    }

    if (!effectiveProjectVuid) {
      setRetrieveError('Please navigate to a project page first to retrieve change orders.');
      return;
    }

    setRetrievingChangeOrders(true);
    setRetrieveError('');

    try {
      const url = `${baseURL}/api/integrations/${selectedIntegration.vuid}/list-prime-change-orders?project_vuid=${effectiveProjectVuid}`;
      
      console.log('Fetching prime change orders from:', url);
      
      const response = await axios.get(url);
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to retrieve change orders');
      }
      
      const changeOrdersArray = response.data.change_orders || [];
      
      if (response.data.errors && response.data.errors.length > 0) {
        console.warn('Errors during retrieval:', response.data.errors);
      }
      
      setRetrievedChangeOrders(changeOrdersArray);
      setSelectedChangeOrders([]); // Reset selection when retrieving new data
    } catch (error) {
      console.error('Error retrieving change orders:', error);
      setRetrieveError(`Error retrieving change orders: ${error.response?.data?.error || error.message}`);
    } finally {
      setRetrievingChangeOrders(false);
    }
  };

  const handleChangeOrderSelection = (changeOrder, isSelected) => {
    if (isSelected) {
      setSelectedChangeOrders([...selectedChangeOrders, changeOrder]);
    } else {
      setSelectedChangeOrders(selectedChangeOrders.filter(co => co.id !== changeOrder.id));
    }
  };

  const handleSelectAllChangeOrders = () => {
    setSelectedChangeOrders([...retrievedChangeOrders]);
  };

  const handleClearAllChangeOrders = () => {
    setSelectedChangeOrders([]);
  };

  const handleImportChangeOrders = async () => {
    if (selectedChangeOrders.length === 0) {
      alert('Please select at least one change order to import');
      return;
    }

    if (!effectiveProjectVuid) {
      alert('Please navigate to a project page first to import change orders');
      return;
    }

    setImportingChangeOrders(true);
    setImportErrors([]);

    try {
      const url = `${baseURL}/api/integrations/${selectedIntegration.vuid}/import-prime-change-orders`;
      
      const response = await axios.post(url, {
        project_vuid: effectiveProjectVuid,
        change_orders: selectedChangeOrders
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to import change orders');
      }

      const imported = response.data.imported || [];
      const errors = response.data.errors || [];

      if (errors.length > 0) {
        setImportErrors(errors);
      }

      if (imported.length > 0) {
        // Refresh the change orders list
        await fetchData();
        // Close modal and show success
        setShowRetrieveModal(false);
        setRetrievedChangeOrders([]);
        setSelectedChangeOrders([]);
        setSuccessMessage(`Successfully imported ${imported.length} change order(s)`);
      }
    } catch (error) {
      console.error('Error importing change orders:', error);
      setImportErrors([`Error importing change orders: ${error.response?.data?.error || error.message}`]);
    } finally {
      setImportingChangeOrders(false);
    }
  };

  const handleUpdateChangeOrder = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${baseURL}/api/external-change-orders/${editingChangeOrder.vuid}`, editFormData);
      setShowEditForm(false);
      setEditingChangeOrder(null);
      setSuccessMessage('External change order updated successfully!');
      fetchData();
    } catch (err) {
      setError('Error updating external change order: ' + (err.response?.data?.error || err.message));
    }
  };

  const closeEditForm = () => {
    setShowEditForm(false);
    setEditingChangeOrder(null);
    setEditFormData({
      project_vuid: '',
      contract_vuid: '',
      accounting_period_vuid: '',
      change_order_number: '',
      description: '',
      change_order_date: '',
      status: '',
      notes: ''
    });
  };

  const closeLinesForm = async () => {
    setShowLines(false);
    setSelectedChangeOrder(null);
    setChangeOrderLines([]);
    setLineFormData({
      cost_code_vuid: '',
      cost_type_vuid: '',
      contract_amount_change: '',
      budget_amount_change: '',
      notes: ''
    });
    
    // Refresh the change orders list when closing the lines form
    await fetchData();
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    const num = parseFloat(amount);
    if (isNaN(num)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(num);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const getProjectName = (projectVuid) => {
    const project = projects.find(p => p.vuid === projectVuid);
    return project ? project.project_name : 'Unknown Project';
  };

  const getContractName = (contractVuid) => {
    const contract = contracts.find(c => c.vuid === contractVuid);
    return contract ? contract.contract_name : 'Unknown Contract';
  };

  const getCostCodeDisplay = (costCodeVuid) => {
    if (!costCodeVuid) return '-';
    const costCode = costCodes.find(cc => cc.vuid === costCodeVuid);
    if (costCode) return `${costCode.code} - ${costCode.description}`;
    const fromBudget = projectBudgetLines.find(
      (bl) => bl.cost_code_vuid === costCodeVuid && bl.cost_code
    );
    if (fromBudget && fromBudget.cost_code) {
      const cc = fromBudget.cost_code;
      return `${cc.code} - ${cc.description || cc.code}`;
    }
    return 'Unknown';
  };

  const getCostTypeDisplay = (costTypeVuid) => {
    const costType = costTypes.find(ct => ct.vuid === costTypeVuid);
    return costType ? costType.abbreviation : 'Unknown';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading External Change Orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">External Change Orders</h1>
          <p className="mt-2 text-gray-600">
            Manage change orders that modify project contract values
          </p>
          {effectiveProjectVuid && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-blue-800 font-medium">
                    Filtered by Project: <span className="font-bold">{getProjectName(effectiveProjectVuid)}</span>
                  </p>
                  <p className="text-blue-600 text-sm mt-1">
                    Showing only change orders for this project
                  </p>
                </div>
                <button
                  onClick={() => navigate('/external-change-orders')}
                  className="bg-gray-50 hover:bg-gray-100 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Show All Projects
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-600 font-medium">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600 font-medium">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="text-center mb-8">
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => {
                // Reset form when opening, but keep project from URL if available
                const searchParams = new URLSearchParams(location.search);
                const urlProjectVuid = searchParams.get('project');
                const effectiveProjectVuid = projectVuid || urlProjectVuid;
                
                setFormData({
                  project_vuid: effectiveProjectVuid || '',
                  contract_vuid: contractVuid || '',
                  accounting_period_vuid: '',
                  change_order_number: '',
                  description: '',
                  change_order_date: new Date().toISOString().split('T')[0],
                  status: 'pending',
                  notes: ''
                });
                setShowCreateForm(true);
              }}
              className="flex items-center space-x-2 bg-white hover:bg-gray-50 font-medium py-2 px-4 rounded text-base text-gray-900 border border-gray-300 transition-colors"
              data-tooltip="Add External Change Order"
            >
              <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Add External Change Order</span>
            </button>
            <button
              onClick={() => {
                if (!effectiveProjectVuid) {
                  alert('Please navigate to a project page first to retrieve change orders.');
                  return;
                }
                setShowRetrieveModal(true);
                setSelectedIntegration(null);
                setRetrievedChangeOrders([]);
                setSelectedChangeOrders([]);
              }}
              className="flex items-center space-x-2 bg-white hover:bg-gray-50 font-medium py-2 px-4 rounded text-base text-gray-900 border border-gray-300 transition-colors"
              data-tooltip="Download"
            >
              <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>Download</span>
            </button>
          </div>
        </div>

        {/* Create Form Modal */}
        {showCreateForm && (
          <div className="bg-white rounded-xl shadow-2xl p-8 mb-12 border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Create External Change Order</h2>
            
            <form onSubmit={handleCreateChangeOrder} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project *
                  </label>
                  <select
                    value={formData.project_vuid}
                    onChange={(e) => {
                      // Clear contract when project changes so it can be repopulated
                      setFormData({
                        ...formData,
                        project_vuid: e.target.value,
                        contract_vuid: '' // Clear contract when project changes
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select Project</option>
                    {projects.map((project) => (
                      <option key={project.vuid} value={project.vuid}>
                        {project.project_name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contract *
                  </label>
                  <select
                    value={formData.contract_vuid}
                    onChange={(e) => setFormData({...formData, contract_vuid: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    disabled={!formData.project_vuid}
                  >
                    <option value="">Select Contract</option>
                    {contracts
                      .filter(contract => !formData.project_vuid || contract.project_vuid === formData.project_vuid)
                      .map((contract) => (
                        <option key={contract.vuid} value={contract.vuid}>
                          {contract.contract_name}
                        </option>
                      ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Accounting Period *
                  </label>
                  <select
                    value={formData.accounting_period_vuid}
                    onChange={(e) => setFormData({...formData, accounting_period_vuid: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select Accounting Period</option>
                    {accountingPeriods
                      .filter(period => period.status === 'open')
                      .map((period) => (
                        <option key={period.vuid} value={period.vuid}>
                          {period.month}/{period.year}
                        </option>
                      ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Change Order Number *
                  </label>
                  <input
                    type="text"
                    value={formData.change_order_number}
                    onChange={(e) => setFormData({...formData, change_order_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., ECO-001"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Change Order Date *
                  </label>
                  <input
                    type="date"
                    value={formData.change_order_date}
                    onChange={(e) => setFormData({...formData, change_order_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  placeholder="Describe the change order..."
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status *
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="2"
                  placeholder="Additional notes..."
                />
              </div>
              
              <div className="text-center pt-6">
                <div className="flex justify-center space-x-4">
                  <button
                    type="submit"
                    className="bg-gray-50 hover:bg-gray-100 font-semibold py-2 px-6 rounded-lg text-base transition-all duration-200 transform hover:scale-105 shadow-lg"
                  >
                    Create Change Order
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-6 rounded-lg text-base transition-all duration-200 transform hover:scale-105 shadow-lg"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Lines Form - Show when managing lines */}
        {showLines && selectedChangeOrder && (() => {
          // Calculate totals from current lines
          const totalContractChange = changeOrderLines.reduce((sum, line) => {
            return sum + (parseFloat(line.contract_amount_change) || 0);
          }, 0);
          const totalBudgetChange = changeOrderLines.reduce((sum, line) => {
            return sum + (parseFloat(line.budget_amount_change) || 0);
          }, 0);
          
          return (
            <>
              {/* Change Order Header Card */}
              <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold text-gray-900">{selectedChangeOrder.change_order_number}</h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedChangeOrder.status)}`}>
                    {selectedChangeOrder.status}
                  </span>
                </div>
                
                <p className="text-gray-600 mb-4">{selectedChangeOrder.description}</p>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Project:</span>
                    <span className="text-gray-900">{getProjectName(selectedChangeOrder.project_vuid)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Contract:</span>
                    <span className="text-gray-900">{getContractName(selectedChangeOrder.contract_vuid)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Date:</span>
                    <span className="text-gray-900">{formatDate(selectedChangeOrder.change_order_date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Contract Change:</span>
                    <span className={`font-semibold ${
                      totalContractChange >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(totalContractChange)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Budget Change:</span>
                    <span className={`font-semibold ${
                      totalBudgetChange >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(totalBudgetChange)}
                    </span>
                  </div>
                </div>
              </div>

            {/* Lines Management Section */}
            <div className="bg-white rounded-xl shadow-2xl p-8 mb-12 border border-gray-200">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">
                    Manage Lines - {selectedChangeOrder.change_order_number}
                  </h2>
                  <p className="text-gray-600 mt-2">
                    {selectedChangeOrder.description}
                  </p>
                </div>
                <button
                  onClick={closeLinesForm}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
              
              {/* Excel-like Grid for Lines */}
            <div ref={excelGridRef} className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Manage Change Order Lines</h3>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleSaveAllLines}
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                  >
                    Save All Lines
                  </button>
                  <button
                    type="button"
                    onClick={handleAddNewLine}
                    className="bg-gray-50 hover:bg-gray-100 font-semibold py-2 px-4 rounded-lg transition-colors"
                  >
                    Add Line
                  </button>
                </div>
              </div>
              
              {/* Excel-like Grid */}
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-300">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold text-gray-900">Line #</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold text-gray-900">Cost Code *</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold text-gray-900">Cost Type *</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold text-gray-900">Contract Amount Change *</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold text-gray-900">Budget Amount Change</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold text-gray-900">Notes</th>
                      <th className="border border-gray-300 px-3 py-2 text-center text-sm font-semibold text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Existing Lines */}
                    {changeOrderLines.map((line, index) => (
                      <tr key={line.vuid} className="hover:bg-gray-50">
                        <td className="border border-gray-300 px-3 py-2 text-sm text-gray-900">{index + 1}</td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-gray-900">
                          <select
                            value={line.cost_code_vuid}
                            onChange={(e) => handleLineUpdate(line.vuid, 'cost_code_vuid', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="">Select Cost Code</option>
                            {lineCostCodeSelectOptions.map((opt) => (
                              <option key={opt.vuid} value={opt.vuid}>
                                {opt.code} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-gray-900">
                          <select
                            value={line.cost_type_vuid}
                            onChange={(e) => handleLineUpdate(line.vuid, 'cost_type_vuid', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="">Select Cost Type</option>
                            {costTypes
                              .filter(t => t.status === 'active')
                              .filter(costType => (
                                projectBudgetLines.length === 0 ||
                                projectBudgetLines.some(budgetLine => 
                                  budgetLine.cost_type_vuid === costType.vuid &&
                                  budgetLine.cost_code_vuid === line.cost_code_vuid
                                )
                              ))
                              .map((costType) => (
                                <option key={costType.vuid} value={costType.vuid}>
                                  {costType.abbreviation} - {costType.cost_type}
                                </option>
                              ))}
                          </select>
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-gray-900">
                          <input
                            type="number"
                            step="0.01"
                            value={line.contract_amount_change}
                            onChange={(e) => handleLineUpdate(line.vuid, 'contract_amount_change', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-gray-900">
                          <input
                            type="number"
                            step="0.01"
                            value={line.budget_amount_change}
                            onChange={(e) => handleLineUpdate(line.vuid, 'budget_amount_change', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-gray-900">
                          <input
                            type="text"
                            value={line.notes || ''}
                            onChange={(e) => handleLineUpdate(line.vuid, 'notes', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Notes..."
                          />
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-center">
                          <button
                            onClick={() => handleDeleteLine(line.vuid)}
                            className="text-red-600 hover:text-red-900 text-sm font-medium"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                    
                    {/* New Line Row */}
                    <tr className="bg-blue-50">
                      <td className="border border-gray-300 px-3 py-2 text-sm text-gray-900 font-medium">
                        {changeOrderLines.length + 1}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-sm text-gray-900">
                        <select
                          value={lineFormData.cost_code_vuid}
                          onChange={(e) => setLineFormData({...lineFormData, cost_code_vuid: e.target.value})}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Select Cost Code</option>
                          {lineCostCodeSelectOptions.map((opt) => (
                            <option key={opt.vuid} value={opt.vuid}>
                              {opt.code} - {opt.description}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-sm text-gray-900">
                        <select
                          value={lineFormData.cost_type_vuid}
                          onChange={(e) => setLineFormData({...lineFormData, cost_type_vuid: e.target.value})}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Select Cost Type</option>
                          {costTypes
                            .filter(t => t.status === 'active')
                            .filter(costType => (
                              projectBudgetLines.length === 0 ||
                              projectBudgetLines.some(budgetLine => 
                                budgetLine.cost_type_vuid === costType.vuid &&
                                budgetLine.cost_code_vuid === lineFormData.cost_code_vuid
                              )
                            ))
                            .map((costType) => (
                              <option key={costType.vuid} value={costType.vuid}>
                                {costType.abbreviation} - {costType.cost_type}
                              </option>
                            ))}
                        </select>
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-sm text-gray-900">
                        <input
                          type="number"
                          step="0.01"
                          value={lineFormData.contract_amount_change}
                          onChange={(e) => setLineFormData({...lineFormData, contract_amount_change: e.target.value})}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="0.00"
                        />
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-sm text-gray-900">
                        <input
                          type="number"
                          step="0.01"
                          value={lineFormData.budget_amount_change}
                          onChange={(e) => setLineFormData({...lineFormData, budget_amount_change: e.target.value})}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="0.00 (optional)"
                        />
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-sm text-gray-900">
                        <input
                          type="text"
                          value={lineFormData.notes}
                          onChange={(e) => setLineFormData({...lineFormData, notes: e.target.value})}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Notes..."
                        />
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-center">
                        <div className="flex gap-2 justify-center">
                          <button
                            onClick={handleAddNewLine}
                            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-1 px-3 rounded text-sm transition-colors"
                          >
                            Add
                          </button>
                          <button
                            onClick={handleClearNewLine}
                            className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-1 px-3 rounded text-sm transition-colors"
                          >
                            Clear
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            </div>
            </>
          );
        })()}

        {/* Edit Form Modal */}
        {showEditForm && (
          <div className="bg-white rounded-xl shadow-2xl p-8 mb-12 border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Edit External Change Order</h2>
            
            <form onSubmit={handleUpdateChangeOrder} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project *
                  </label>
                  <select
                    value={editFormData.project_vuid}
                    onChange={(e) => setEditFormData({...editFormData, project_vuid: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Project</option>
                    {projects.map((project) => (
                      <option key={project.vuid} value={project.vuid}>
                        {project.project_number} - {project.project_name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contract
                  </label>
                  <select
                    value={editFormData.contract_vuid}
                    onChange={(e) => setEditFormData({...editFormData, contract_vuid: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Contract</option>
                    {contracts
                      .filter(contract => contract.project_vuid === editFormData.project_vuid)
                      .map((contract) => (
                        <option key={contract.vuid} value={contract.vuid}>
                          {contract.contract_number} - {contract.contract_name}
                        </option>
                      ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Accounting Period
                  </label>
                  <select
                    value={editFormData.accounting_period_vuid}
                    onChange={(e) => setEditFormData({...editFormData, accounting_period_vuid: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Period</option>
                    {accountingPeriods.map((period) => (
                      <option key={period.vuid} value={period.vuid}>
                        {period.month}/{period.year} - {period.status}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Change Order Number *
                  </label>
                  <input
                    type="text"
                    value={editFormData.change_order_number}
                    onChange={(e) => setEditFormData({...editFormData, change_order_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Change Order Date *
                  </label>
                  <input
                    type="date"
                    value={editFormData.change_order_date}
                    onChange={(e) => setEditFormData({...editFormData, change_order_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status *
                </label>
                <select
                  value={editFormData.status}
                  onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>
              
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={closeEditForm}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-6 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-gray-50 hover:bg-gray-100 font-semibold py-2 px-6 rounded-lg transition-colors"
                >
                  Update Change Order
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Change Orders Grid - Only show when not managing lines */}
        {!showCreateForm && !showEditForm && !showLines && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              External Change Orders
              {effectiveProjectVuid && (
                <span className="text-lg font-normal text-gray-600 ml-2">
                  - {getProjectName(effectiveProjectVuid)}
                </span>
              )}
            </h2>
            
            {externalChangeOrders.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-gray-400 text-6xl mb-4">📋</div>
                <p className="text-gray-500 text-xl mb-4">No external change orders found.</p>
                <p className="text-gray-400 mb-6">Get started by creating your first external change order.</p>
                <button
                  onClick={() => {
                    // Reset form when opening, but keep project from URL if available
                    const searchParams = new URLSearchParams(location.search);
                    const urlProjectVuid = searchParams.get('project');
                    const effectiveProjectVuid = projectVuid || urlProjectVuid;
                    
                    setFormData({
                      project_vuid: effectiveProjectVuid || '',
                      contract_vuid: contractVuid || '',
                      accounting_period_vuid: '',
                      change_order_number: '',
                      description: '',
                      change_order_date: new Date().toISOString().split('T')[0],
                      status: 'pending',
                      notes: ''
                    });
                    setShowCreateForm(true);
                  }}
                  className="bg-gray-50 hover:bg-gray-100 font-semibold py-2 px-6 rounded-lg transition-colors text-lg"
                >
                  Create First External Change Order
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {externalChangeOrders.map((changeOrder) => (
                  <div
                    key={changeOrder.vuid}
                    className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-lg transition-shadow"
                  >
                    {/* Header */}
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-xl font-bold text-gray-900">{changeOrder.change_order_number}</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(changeOrder.status)}`}>
                        {changeOrder.status}
                      </span>
                    </div>
                    
                    {/* Description */}
                    <p className="text-gray-600 mb-4 line-clamp-2">{changeOrder.description}</p>
                    
                    {/* Key Info */}
                    <div className="space-y-3 mb-4">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Project:</span>
                        <span className="text-gray-900">{getProjectName(changeOrder.project_vuid)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Contract:</span>
                        <span className="text-gray-900">{getContractName(changeOrder.contract_vuid)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Date:</span>
                        <span className="text-gray-900">{formatDate(changeOrder.change_order_date)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Contract Change:</span>
                        <span className={`font-semibold ${
                          parseFloat(changeOrder.total_contract_change_amount || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatCurrency(changeOrder.total_contract_change_amount || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Budget Change:</span>
                        <span className={`font-semibold ${
                          parseFloat(changeOrder.total_budget_change_amount || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatCurrency(changeOrder.total_budget_change_amount || 0)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditChangeOrder(changeOrder)}
                        className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                        data-tooltip="Edit change order"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => {
                          console.log('Button clicked, changeOrder:', changeOrder);
                          console.log('changeOrder.vuid:', changeOrder.vuid);
                          handleShowLines(changeOrder);
                        }}
                        className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                        data-tooltip="Manage Lines"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteChangeOrder(changeOrder.vuid)}
                        className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                        data-tooltip="Delete change order"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Retrieve from Integration Modal */}
        {showRetrieveModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Retrieve Prime Change Orders</h2>
                <button
                  onClick={() => {
                    setShowRetrieveModal(false);
                    setRetrievedChangeOrders([]);
                    setRetrieveError('');
                    setSelectedIntegration(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                {!effectiveProjectVuid && (
                  <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-yellow-800">
                      Please navigate to a project page first to retrieve change orders.
                    </p>
                  </div>
                )}

                {effectiveProjectVuid && (
                  <>
                    {/* Integration Selection */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Select Integration:
                      </label>
                      {(() => {
                        if (integrations.length === 0) {
                          return (
                            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <p className="text-yellow-800">
                                No active integrations found. Please configure an integration first.
                              </p>
                            </div>
                          );
                        }
                        return (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {integrations.map((integration) => (
                              <div
                                key={integration.vuid}
                                onClick={() => {
                                  setSelectedIntegration(integration);
                                  setRetrievedChangeOrders([]);
                                  setRetrieveError('');
                                }}
                                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                  selectedIntegration?.vuid === integration.vuid
                                    ? 'border-gray-800 bg-gray-50'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h4 className="font-semibold text-gray-900">{integration.integration_name}</h4>
                                    <p className="text-sm text-gray-600">{integration.integration_type}</p>
                                  </div>
                                  {selectedIntegration?.vuid === integration.vuid && (
                                    <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Error Message */}
                    {retrieveError && (
                      <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-800">{retrieveError}</p>
                      </div>
                    )}

                    {/* Retrieved Change Orders List */}
                    {retrievedChangeOrders.length > 0 && (
                      <div className="mb-6">
                        <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                                {retrievedChangeOrders.length} Approved Prime Change Orders Found
                              </h3>
                              <p className="text-blue-700 text-sm">
                                <strong>What does "Approved" mean?</strong> An approved prime change order is one that has been approved and is ready for integration.
                              </p>
                              <p className="text-blue-700 mt-2">
                                Selected: {selectedChangeOrders.length} change order{selectedChangeOrders.length !== 1 ? 's' : ''}
                              </p>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={handleSelectAllChangeOrders}
                                className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-all duration-200 transform hover:scale-105 shadow-lg"
                              >
                                Select All
                              </button>
                              <button
                                onClick={handleClearAllChangeOrders}
                                className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-all duration-200 transform hover:scale-105 shadow-lg"
                              >
                                Clear All
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-3">
                          {retrievedChangeOrders.map((co, index) => {
                            const isSelected = selectedChangeOrders.some(selected => selected.id === co.id);
                            return (
                              <div
                                key={index}
                                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                  isSelected
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                                onClick={() => handleChangeOrderSelection(co, !isSelected)}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-3 mb-2">
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={(e) => handleChangeOrderSelection(co, e.target.checked)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <h4 className="font-semibold text-gray-900">
                                        {co.change_order_number || 'Unknown Number'}
                                      </h4>
                                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(co.status)}`}>
                                        {co.status || 'Unknown'}
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-600 mb-2">
                                      {co.description || 'No description'}
                                    </p>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                      <div>
                                        <span className="font-medium text-gray-500">Contract:</span>
                                        <span className="ml-1 text-gray-900">
                                          {co.contract_number || co.contract_name || 'Unknown'}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-500">Amount:</span>
                                        <span className="ml-1 text-gray-900">
                                          {formatCurrency(co.grand_total || co.total_amount || 0)}
                                        </span>
                                      </div>
                                      {co.line_items && co.line_items.length > 0 && (
                                        <div>
                                          <span className="font-medium text-gray-500">Line Items:</span>
                                          <span className="ml-1 text-gray-900">
                                            {co.line_items.length}
                                          </span>
                                        </div>
                                      )}
                                      {(co.change_order_date || co.created_at) && (
                                        <div>
                                          <span className="font-medium text-gray-500">Date:</span>
                                          <span className="ml-1 text-gray-900">
                                            {formatDate(co.change_order_date || co.created_at)}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {isSelected && (
                                    <svg className="w-5 h-5 text-blue-600 ml-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {importErrors.length > 0 && (
                          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-red-800 font-medium mb-2">Import Errors:</p>
                            <ul className="list-disc list-inside text-red-700 text-sm">
                              {importErrors.map((error, idx) => (
                                <li key={idx}>{error}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* No Change Orders Message */}
                    {retrievedChangeOrders.length === 0 && !retrievingChangeOrders && selectedIntegration && (
                      <div className="mb-6 p-4 bg-gray-50 rounded-lg text-center">
                        <p className="text-gray-600">
                          Click "Retrieve Change Orders" to fetch approved prime change orders from your integration.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => {
                    setShowRetrieveModal(false);
                    setRetrievedChangeOrders([]);
                    setRetrieveError('');
                    setSelectedIntegration(null);
                    setSelectedChangeOrders([]);
                    setImportErrors([]);
                  }}
                  className="px-8 py-4 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Cancel
                </button>
                <div className="flex flex-wrap justify-end gap-3">
                  {retrievedChangeOrders.length > 0 && (
                    <button
                      onClick={handleRetrieveChangeOrders}
                      disabled={retrievingChangeOrders}
                      className={`px-8 py-4 font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg ${
                        !retrievingChangeOrders
                          ? 'bg-gray-800 text-white hover:bg-gray-900'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {retrievingChangeOrders ? 'Retrieving...' : 'Refresh'}
                    </button>
                  )}
                  <button
                    onClick={handleRetrieveChangeOrders}
                    disabled={!selectedIntegration || retrievingChangeOrders}
                    className={`px-8 py-4 font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg ${
                      selectedIntegration && !retrievingChangeOrders
                        ? 'bg-gray-800 text-white hover:bg-gray-900'
                        : retrievingChangeOrders
                        ? 'bg-gray-800 text-white cursor-wait'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {retrievingChangeOrders ? (
                      <div className="flex items-center space-x-2">
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 + 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Retrieving...</span>
                      </div>
                    ) : (
                      'Retrieve Change Orders'
                    )}
                  </button>
                  {retrievedChangeOrders.length > 0 && (
                    <button
                      onClick={handleImportChangeOrders}
                      disabled={selectedChangeOrders.length === 0 || importingChangeOrders}
                      className={`px-8 py-4 font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg ${
                        selectedChangeOrders.length > 0 && !importingChangeOrders
                          ? 'bg-gray-800 text-white hover:bg-gray-900'
                          : importingChangeOrders
                          ? 'bg-gray-800 text-white cursor-wait'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {importingChangeOrders ? (
                        <div className="flex items-center space-x-2">
                          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 + 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Importing...</span>
                        </div>
                      ) : (
                        `Import (${selectedChangeOrders.length})`
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExternalChangeOrders;
